// @ts-strict-ignore
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Button,
  Icon,
  RowsPerPageType,
  Sheet,
  Box as Wrapper,
} from '@node-space/ui-components'
import { PageHeader } from 'components/header/PageHeader'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useBeneficiariesQuery } from 'hooks/queries/useBeneficiariesQuery'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useSimplePagination } from 'hooks/useSimplePagination'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import {
  AddBeneficiaryFormPayload,
  BeneficiaryDetails,
  BeneficiaryTransferDestination,
} from 'types/beneficiaries'
import { AccountTypes } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { useMappedRoles } from '../../hooks/useMappedRoles'
import { AddBeneficiary, ModalStep } from './AddBeneficiary/AddBeneficiary'
import BeneficiaryTable from './components/BeneficiaryTable'
import { DeleteBeneficiary } from './DeleteBeneficiary/DeleteBeneficiary'
import { beneficiaryToUpdateAtom } from './EditBeneficiary/atoms.ts/beneficiaryToUpdateAtom'
import { EditBeneficiarySheet } from './EditBeneficiary/EditBeneficiarySheet'
import { useFeatureFlags } from '@node-space/hooks'

export const ManageBeneficiaries = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const permissions = useMappedRoles()
  const { enableLegacyBeneficiaryCreation } = useFeatureFlags()
  const { profile } = useProfileContext() || {}
  const { options: profileOptions } = profile || {}
  const { twoFactorEnabled: isTwoFactorEnabled } = useTwoFAContext() || {}
  const callbackUrl = searchParams.get('callbackUrl') ?? null

  const setBeneficiaryToUpdate = useSetAtom(beneficiaryToUpdateAtom)

  const [showEditBeneficiarySheet, setShowEditBeneficiarySheet] = useState(false)

  const isDeveloperAccount = profileOptions?.registrationAccountType === AccountTypes.DEVELOPER
  const canEdit = permissions?.manageBeneficiaries?.canEdit
  const { data: countries } = useCountriesQuery(true)

  const sizeMax = +searchParams.get('size')
  const getValidRowsPerPage = (size: number): RowsPerPageType => {
    const validSizes: RowsPerPageType[] = [10, 20, 30, 40, 50, 100]
    return validSizes.includes(size as RowsPerPageType) ? (size as RowsPerPageType) : 10
  }

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = useSimplePagination(
    getValidRowsPerPage(sizeMax)
  )

  const beneficiariesQuery = useBeneficiariesQuery({
    page,
    size: rowsPerPage,
  })

  const {
    data: { content: beneficiaryList, pagination } = {},
    isPending,
    isError,
  } = beneficiariesQuery

  // Add Beneficiary
  const [isAddBeneficiarySheetOpen, setIsAddBeneficiarySheetOpen] = useState(false)
  const [addBeneficiaryStep, setAddBeneficiaryStep] = useState<ModalStep>(ModalStep.TRANSFER_TYPE)
  const [beneficiaryFormState, setBeneficiaryFormState] = useState<AddBeneficiaryFormPayload>(
    {} as AddBeneficiaryFormPayload
  )

  // Delete Beneficiary
  const [beneficiaryToDelete, setBeneficiaryToDelete] = useState<BeneficiaryDetails>(undefined)
  useEffect(() => {
    track.Amp.track(AmplitudeEvent.MANAGE_BENEFICIARIES, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const addBeneficiarySheetHeader = useMemo(() => {
    if (addBeneficiaryStep === ModalStep.INTERNATIONAL_BANK_DETAILS) {
      return t('manageBeneficiaries.beneficiaryBankDetails')
    }
    if (addBeneficiaryStep === ModalStep.INTERMEDIARY_BANK_DETAILS) {
      return t('manageBeneficiaries.intermediaryBankDetails')
    }

    if (addBeneficiaryStep === ModalStep.CONFIRMATION) {
      return t('confirmBeneficiaryDetails')
    }

    return t('manageBeneficiaries.addBeneficiaryTitle')
  }, [addBeneficiaryStep])

  const handleCloseAddBeneficiarySheet = useCallback(() => {
    setIsAddBeneficiarySheetOpen(false)
    setBeneficiaryFormState({} as AddBeneficiaryFormPayload)
    setAddBeneficiaryStep(ModalStep.TRANSFER_TYPE)
    searchParams?.delete('callbackUrl')
    window.history.pushState({}, document.title, callbackUrl)
  }, [setIsAddBeneficiarySheetOpen])

  const handleOnSuccessCloseModal = useCallback(() => {
    handleCloseAddBeneficiarySheet()

    if (callbackUrl) {
      searchParams?.delete('callbackUrl')
      window.history.pushState({}, document.title, callbackUrl)
      window.location.pathname = callbackUrl
    }
  }, [setIsAddBeneficiarySheetOpen, callbackUrl])

  const onAddBeneficiaryBackHandler = useCallback(() => {
    switch (addBeneficiaryStep) {
      case ModalStep.TRANSFER_TYPE:
        handleCloseAddBeneficiarySheet()
        break
      case ModalStep.ADD_BENEFICIARY:
        setAddBeneficiaryStep(ModalStep.TRANSFER_TYPE)
        break
      case ModalStep.INTERNATIONAL_BANK_DETAILS:
        setAddBeneficiaryStep(ModalStep.ADD_BENEFICIARY)
        break
      case ModalStep.INTERMEDIARY_BANK_DETAILS:
        setAddBeneficiaryStep(ModalStep.INTERNATIONAL_BANK_DETAILS)
        break
      case ModalStep.CONFIRMATION:
        if (beneficiaryFormState?.transferDestination === BeneficiaryTransferDestination.LOCAL) {
          setAddBeneficiaryStep(ModalStep.ADD_BENEFICIARY)
        } else {
          setAddBeneficiaryStep(ModalStep.INTERMEDIARY_BANK_DETAILS)
        }
        break
      default:
        break
    }
  }, [
    addBeneficiaryStep,
    beneficiaryFormState,
    handleCloseAddBeneficiarySheet,
    setAddBeneficiaryStep,
  ])

  const handleResetDeleteBeneficiary = useCallback(
    () => setBeneficiaryToDelete(undefined),
    [setBeneficiaryToDelete]
  )

  const handleEditBeneficiary = useCallback(
    (beneficiary: BeneficiaryDetails) => {
      setBeneficiaryToUpdate({
        beneficiary,
        onBack: () => {
          setBeneficiaryToUpdate(null)
        },
        onClose: () => {
          setShowEditBeneficiarySheet(false)
          setBeneficiaryToUpdate(null)
        },
      })
      setShowEditBeneficiarySheet(true)
    },
    [setBeneficiaryToUpdate]
  )

  const canAddBeneficiary = useMemo(() => {
    return canEdit && beneficiaryList?.length > 0 && enableLegacyBeneficiaryCreation
  }, [canEdit, beneficiaryList?.length, enableLegacyBeneficiaryCreation])

  return (
    <Wrapper>
      <PageHeader
        description={t('manageBeneficiaries.description')}
        renderRightSection={
          canAddBeneficiary && (
            <Button
              onClick={() => setIsAddBeneficiarySheetOpen(true)}
              iconElement={<Icon name="Plus" />}
              disabled={isDeveloperAccount || !isTwoFactorEnabled}
            >
              {t('manageBeneficiaries.addBeneficiary')}
            </Button>
          )
        }
      />

      <BeneficiaryTable
        setBeneficiaryToDelete={setBeneficiaryToDelete}
        setIsAddModalOpen={setIsAddBeneficiarySheetOpen}
        handleEditBeneficiary={handleEditBeneficiary}
        beneficiaryList={beneficiaryList}
        pagination={pagination}
        isPending={isPending}
        isError={isError}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />

      <Sheet
        isOpen={isAddBeneficiarySheetOpen}
        header={addBeneficiarySheetHeader}
        onBack={onAddBeneficiaryBackHandler}
        onClose={handleCloseAddBeneficiarySheet}
        onOpenChange={setIsAddBeneficiarySheetOpen}
      >
        <Box width="full" paddingY={24}>
          <AddBeneficiary
            addBeneficiaryStep={addBeneficiaryStep}
            setAddBeneficiaryStep={setAddBeneficiaryStep}
            handleCloseModal={handleCloseAddBeneficiarySheet}
            handleOnSuccessCloseModal={handleOnSuccessCloseModal}
            beneficiaryFormState={beneficiaryFormState}
            setBeneficiaryFormState={setBeneficiaryFormState}
          />
        </Box>
      </Sheet>

      {!!beneficiaryToDelete && (
        <DeleteBeneficiary
          beneficiaryDetails={beneficiaryToDelete}
          countries={countries}
          onClose={handleResetDeleteBeneficiary}
        />
      )}

      <EditBeneficiarySheet isOpen={showEditBeneficiarySheet} isNestedSheet={false} />
    </Wrapper>
  )
}
